import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Save, FileCheck } from 'lucide-react';
import { isDriveUrl, toThumbnailUrl } from '@/utils/drive';

export default function DiplomaEditor() {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [form, setForm] = useState({
    template_url: '',
    name_position_x: 50,
    name_position_y: 50,
    font_size: 32,
    font_color: '#000000',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.getCourse(courseId),
      api.getDiplomaTemplate(courseId).catch(() => ({ data: null })),
    ]).then(([cRes, tRes]) => {
      setCourse(cRes.data);
      if (tRes.data) {
        setForm({
          template_url: tRes.data.template_url || '',
          name_position_x: tRes.data.name_position_x ?? 50,
          name_position_y: tRes.data.name_position_y ?? 50,
          font_size: tRes.data.font_size ?? 32,
          font_color: tRes.data.font_color || '#000000',
        });
      }
    }).catch(() => toast.error('Error al cargar'));
  }, [courseId]);

  const handleSave = async () => {
    if (!form.template_url) { toast.error('URL de plantilla requerida'); return; }
    setLoading(true);
    try {
      await api.upsertDiplomaTemplate({
        course_id: courseId,
        template_url: form.template_url,
        name_position_x: parseInt(form.name_position_x),
        name_position_y: parseInt(form.name_position_y),
        font_size: parseInt(form.font_size),
        font_color: form.font_color,
      });
      toast.success('Plantilla guardada');
      navigate('/admin/courses');
    } catch (e) {
      toast.error('Error al guardar');
    } finally { setLoading(false); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl" data-testid="diploma-editor-page">
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" onClick={() => navigate('/admin/courses')} className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" /> Volver
          </Button>
          <Button onClick={handleSave} disabled={loading} data-testid="save-diploma-button" className="bg-[#DC143C] hover:bg-[#B91C1C] text-white flex items-center gap-2">
            <Save className="h-4 w-4" /> Guardar
          </Button>
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-2 uppercase flex items-center gap-3">
          <FileCheck className="h-7 w-7 text-[#DC143C]" />
          Diploma
        </h1>
        {course && <p className="text-gray-600 mb-6">Curso: <span className="font-medium">{course.title}</span></p>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
            <div>
              <Label className="text-sm font-medium uppercase tracking-wide">URL plantilla *</Label>
              <Input data-testid="diploma-url-input" value={form.template_url} onChange={(e) => setForm({ ...form, template_url: e.target.value })} placeholder="https://..." className="h-11" />
              <p className="text-xs text-gray-500 mt-1">
                Imagen base del diploma (.png o .jpg). Soporta enlaces de Google Drive — usa "Compartir → Cualquier persona con el enlace".
              </p>
              {form.template_url && isDriveUrl(form.template_url) && (
                <p className="text-xs text-blue-600 mt-1">
                  Detectada URL de Drive — se convierte automáticamente a un endpoint de miniatura para vista previa.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium uppercase tracking-wide">Posición X (%)</Label>
                <Input type="number" min="0" max="100" value={form.name_position_x} onChange={(e) => setForm({ ...form, name_position_x: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm font-medium uppercase tracking-wide">Posición Y (%)</Label>
                <Input type="number" min="0" max="100" value={form.name_position_y} onChange={(e) => setForm({ ...form, name_position_y: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium uppercase tracking-wide">Tamaño fuente</Label>
                <Input type="number" min="10" max="100" value={form.font_size} onChange={(e) => setForm({ ...form, font_size: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm font-medium uppercase tracking-wide">Color</Label>
                <Input type="color" value={form.font_color} onChange={(e) => setForm({ ...form, font_color: e.target.value })} className="h-11" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <Label className="text-sm font-medium uppercase tracking-wide mb-2 block">Vista previa</Label>
            <div className="relative w-full bg-gray-100 rounded overflow-hidden" style={{ aspectRatio: '4 / 3' }}>
              {form.template_url ? (
                <img src={toThumbnailUrl(form.template_url, 1200)} alt="Plantilla" className="absolute inset-0 w-full h-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">Sin plantilla</div>
              )}
              <div
                className="absolute pointer-events-none -translate-x-1/2 -translate-y-1/2 font-bold"
                style={{
                  left: `${form.name_position_x}%`,
                  top: `${form.name_position_y}%`,
                  fontSize: `${Math.max(12, form.font_size / 3)}px`,
                  color: form.font_color,
                }}
              >
                NOMBRE COMPLETO
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
